import {
  applyClientConditions,
  buildInitialFormValues,
  Button,
  Modal,
  PatientFormView,
  buildMediaContextFromSession,
  getThemeCssVars,
  pfAssignmentLabel,
  Segmented,
  splitFormHeader,
  useToast,
  type FormFillSession as UiFormFillSession,
  type PfPatientFormRecord,
} from '@elva/ui';
import { isAxiosError } from 'axios';
import { useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { getBlob } from '@/api/client';
import { extractApiErrorMessage } from '@/api/errors';
import { ElvaPageLoader } from '@/components/ElvaPageLoader';
import type { FormSendChannel, SyncStatus } from '@/features/forms/api/types';
import { elementToPdf } from '@/features/forms/utils/elementToPdf';
import {
  useApproveSubmission,
  useMarkIncompleteAssignment,
  useResendSubmission,
} from '@/features/forms/hooks/useFormSubmission';
import { logPatientWire } from '../debug/logPatientWire';
import { usePatientFormAccessToken } from '../hooks/usePatientFormAccessToken';
import { usePatientFormDetailSession } from '../hooks/usePatientFormDetailSession';
import { useResolvedFormMediaUrls } from '../hooks/useResolvedFormMediaUrls';

export interface PatientFormDetailDrawerProps {
  form: PfPatientFormRecord | null;
  patientId?: number | null;
  onClose: () => void;
}

const MODAL_CLASS =
  'flex max-h-[min(92vh,920px)] w-[min(820px,calc(100vw-32px))] flex-col overflow-hidden [&>div:nth-child(2)]:min-h-0 [&>div:nth-child(2)]:flex-1 [&>div:nth-child(2)]:overflow-y-auto [&>div:nth-child(2)]:bg-[var(--cream-0)] [&>div:nth-child(2)]:p-0';

function titleCase(status: string | undefined): string {
  if (!status) return 'Unknown';
  return status.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

/** The value is `failed`, never `sync_failed`. "Sync failed" is a label, not a vocabulary. */
function syncLabel(status: SyncStatus | undefined): string {
  switch (status) {
    case 'synced':
      return 'Synced';
    case 'syncing':
      return 'Syncing';
    case 'failed':
      return 'Sync failed';
    case 'not_started':
      return 'Not started';
    default:
      return '—';
  }
}

function syncTone(status: SyncStatus | undefined): string {
  if (status === 'synced') return 'var(--success)';
  if (status === 'failed') return 'var(--terracotta)';
  if (status === 'syncing') return 'var(--gold)';
  return 'var(--ink-3)';
}

function pickSubmissionId(form: PfPatientFormRecord, sessionSubmissionId?: string): string | undefined {
  const fromForm = form.submissionId?.trim();
  if (fromForm) return fromForm;
  const fromSession = sessionSubmissionId?.trim();
  if (fromSession) return fromSession;
  return undefined;
}

function sanitizeFileName(name: string): string {
  const trimmed = name.trim().replace(/[\\/:*?"<>|]+/g, '-');
  return trimmed.length > 0 ? trimmed : 'form';
}

/**
 * Never navigate the tab to `pdfDownloadUrl` — for a relative API path that leaves the
 * request without the staff bearer token (window.open carries no auth header), and for
 * an absolute signed URL it leaves the link sitting in browser history/tabs. Fetch the
 * bytes ourselves (through the authed client for same-origin paths) and save via a
 * throwaway object URL instead.
 */
async function downloadPdfSecurely(url: string, fileName: string): Promise<void> {
  const isSameOrigin = url.startsWith('/');
  const blob = isSameOrigin
    ? await getBlob(url)
    : await (async () => {
        const res = await fetch(url, { credentials: 'omit', referrerPolicy: 'no-referrer' });
        if (!res.ok) throw new Error(`Download failed (${res.status})`);
        return res.blob();
      })();

  const objectUrl = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = objectUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

/**
 * The filed PDF is a patient-record document, so it always renders in a light theme and at a
 * fixed width — never in whatever theme or modal size the staff member happens to be using.
 */
const PDF_THEME_ID = 'sandstone';
const PDF_BACKGROUND = '#FFFFFF';

/** Off-screen host. It is never captured itself — the screenshot clone would inherit the offset. */
const PDF_HOST_STYLE: CSSProperties = {
  position: 'fixed',
  top: 0,
  left: -10000,
  pointerEvents: 'none',
};

const PDF_SNAPSHOT_STYLE = {
  ...getThemeCssVars(PDF_THEME_ID),
  '--sh-2': 'none',
  colorScheme: 'light',
  width: 760,
  background: PDF_BACKGROUND,
} as CSSProperties;

function isPlanNotAcknowledged(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 409) return false;
  const data = error.response.data as { error?: string } | undefined;
  return data?.error === 'plan_not_acknowledged';
}

export function PatientFormDetailDrawer({ form, patientId, onClose }: PatientFormDetailDrawerProps) {
  const { toast } = useToast();
  const { data: session, isLoading, isError, error, refetch } = usePatientFormDetailSession(form);
  const sessionAssignmentId = session?.assignment.id || session?.submission.assignment_id;
  const { accessToken, isLoading: isTokenLoading } = usePatientFormAccessToken(
    form,
    sessionAssignmentId,
  );
  const resolvedMedia = useResolvedFormMediaUrls(
    session as unknown as UiFormFillSession | null | undefined,
    accessToken,
    !isTokenLoading,
  );

  const approve = useApproveSubmission(patientId);
  const markIncomplete = useMarkIncompleteAssignment(patientId);
  const resend = useResendSubmission(patientId);

  const [resendOpen, setResendOpen] = useState(false);
  const [resendChannel, setResendChannel] = useState<FormSendChannel>('sms');
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [isRenderingPdf, setIsRenderingPdf] = useState(false);
  const pdfSnapshotResolver = useRef<((node: HTMLElement) => void) | null>(null);

  const values = useMemo(
    () => (session ? buildInitialFormValues(session as unknown as UiFormFillSession) : {}),
    [session],
  );

  const mediaContext = useMemo(() => {
    if (!session) return undefined;
    const base = buildMediaContextFromSession(session as unknown as UiFormFillSession);
    return {
      ...base,
      fileUrlsByKey: { ...base.fileUrlsByKey, ...resolvedMedia.data?.fileUrlsByKey },
      fileUrlsByBlockId: { ...base.fileUrlsByBlockId, ...resolvedMedia.data?.fileUrlsByBlockId },
      signatureUrlsByBlockId: {
        ...base.signatureUrlsByBlockId,
        ...resolvedMedia.data?.signatureUrlsByBlockId,
      },
      ...(accessToken ? { accessToken } : {}),
    };
  }, [session, accessToken, resolvedMedia.data]);

  const viewModel = useMemo(() => {
    if (!session || !form) return null;
    const uiSession = session as unknown as UiFormFillSession;
    // The server's `is_visible` can disagree with the stored answers (e.g. a follow-up
    // shown because "Other" is ticked), so re-evaluate like the patient-fill app does.
    const liveForm = applyClientConditions(uiSession.resolved_form, values);
    const visibleBlocks = liveForm.blocks.filter((block) => block.is_visible);
    const header = splitFormHeader(visibleBlocks, uiSession.form.name || form.name || 'Form');
    const submission = session.submission;
    const pdfDownloadUrl =
      submission.document?.pdf_download_url ?? submission.document?.url ?? submission.document_url ?? undefined;

    return {
      intro: header.intro,
      body: header.body,
      submissionStatus: submission.status,
      assignmentStatus: session.assignment.status,
      syncStatus: submission.sync_status,
      submissionId: pickSubmissionId(form, submission.id),
      assignmentId: session.assignment.id || submission.assignment_id,
      writePlanVersion: submission.write_plan_version ?? 0,
      pdfDownloadUrl,
    };
  }, [session, form, values]);

  const isSubmitted = viewModel?.submissionStatus === 'submitted';
  const isApproving = approve.isPending || isRenderingPdf;

  /** Mounts the off-screen snapshot, renders it to a PDF, then unmounts it. */
  const renderSubmissionPdf = async (): Promise<Blob> => {
    setIsRenderingPdf(true);
    try {
      const node = await new Promise<HTMLElement>((resolve) => {
        pdfSnapshotResolver.current = resolve;
      });
      return await elementToPdf(node, PDF_BACKGROUND);
    } finally {
      pdfSnapshotResolver.current = null;
      setIsRenderingPdf(false);
    }
  };

  const handleApprove = async () => {
    if (!viewModel?.submissionId) return;

    let pdfFile: Blob;
    try {
      pdfFile = await renderSubmissionPdf();
    } catch (pdfError) {
      toast({
        title: 'Could not create the PDF',
        description: extractApiErrorMessage(pdfError),
        variant: 'error',
      });
      return;
    }

    const submissionId = viewModel.submissionId;
    const submitApproval = (planVersion: number) => {
      logPatientWire(`POST /api/forms/submissions/${submissionId}/approve`, {
        acknowledged_plan_version: planVersion,
        pdf_file: `${String(pdfFile.size)} bytes`,
      });
      return approve.mutateAsync({
        submissionId,
        body: { pdf_file: pdfFile, acknowledged_plan_version: planVersion },
      });
    };

    try {
      try {
        await submitApproval(viewModel.writePlanVersion);
      } catch (approveError) {
        // Syncable fields sync on their own, so staff have nothing to re-confirm when the
        // server rebuilt the write plan meanwhile — approve once more against its version.
        if (!isPlanNotAcknowledged(approveError)) throw approveError;
        const fresh = await refetch();
        await submitApproval(fresh.data?.submission.write_plan_version ?? viewModel.writePlanVersion);
      }
      toast({ title: 'Form approved', variant: 'success' });
      onClose();
    } catch (approveError) {
      toast({
        title: 'Approve failed',
        description: extractApiErrorMessage(approveError),
        variant: 'error',
      });
    }
  };

  const handleMarkIncomplete = async () => {
    if (!viewModel?.assignmentId) return;
    logPatientWire(`POST /api/forms/assignments/${viewModel.assignmentId}/mark-incomplete`, null);
    try {
      await markIncomplete.mutateAsync({
        assignmentId: viewModel.assignmentId,
        ...(viewModel.submissionId ? { submissionId: viewModel.submissionId } : {}),
      });
      toast({ title: 'Marked incomplete', variant: 'success' });
      onClose();
    } catch (incompleteError) {
      toast({
        title: 'Could not mark incomplete',
        description: extractApiErrorMessage(incompleteError),
        variant: 'error',
      });
    }
  };

  const handleDownloadPdf = async () => {
    if (!viewModel?.pdfDownloadUrl || !form) return;
    setIsDownloadingPdf(true);
    try {
      await downloadPdfSecurely(viewModel.pdfDownloadUrl, `${sanitizeFileName(form.name)}.pdf`);
    } catch (downloadError) {
      toast({
        title: 'Download failed',
        description: extractApiErrorMessage(downloadError),
        variant: 'error',
      });
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  const handleResend = async () => {
    if (!viewModel?.submissionId) return;
    logPatientWire(`POST /api/forms/submissions/${viewModel.submissionId}/resend`, {
      channel: resendChannel,
    });
    try {
      await resend.mutateAsync({ submissionId: viewModel.submissionId, body: { channel: resendChannel } });
      toast({ title: `Resent by ${resendChannel === 'sms' ? 'text' : 'email'}`, variant: 'success' });
      setResendOpen(false);
      onClose();
    } catch (resendError) {
      toast({
        title: 'Resend failed',
        description: extractApiErrorMessage(resendError),
        variant: 'error',
      });
    }
  };

  if (!form) return null;

  return (
    <Modal
      title={form.name}
      onClose={onClose}
      className={MODAL_CLASS}
      footer={
        viewModel ? (
          <div className="flex w-full flex-col gap-2.5">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-[var(--ink-3)]">
              <span>
                Assignment:{' '}
                <strong className="text-[var(--ink-1)]">{titleCase(viewModel.assignmentStatus)}</strong>
              </span>
              <span>
                Submission:{' '}
                <strong className="text-[var(--ink-1)]">{titleCase(viewModel.submissionStatus)}</strong>
              </span>
              <span>
                Sync:{' '}
                <strong style={{ color: syncTone(viewModel.syncStatus) }}>
                  {syncLabel(viewModel.syncStatus)}
                </strong>
              </span>
              <span>List status: {pfAssignmentLabel(form)}</span>
            </div>

            {resendOpen ? (
              <div className="flex flex-wrap items-center justify-end gap-2">
                <span className="mr-auto text-[12px] text-[var(--ink-3)]">Resend the link via:</span>
                <Segmented
                  options={[
                    { value: 'sms', label: 'Text' },
                    { value: 'email', label: 'Email' },
                  ]}
                  value={resendChannel}
                  onChange={(value) => {
                    setResendChannel(value as FormSendChannel);
                  }}
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setResendOpen(false);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  iconLeft="send"
                  disabled={resend.isPending}
                  onClick={() => {
                    void handleResend();
                  }}
                >
                  {resend.isPending ? 'Resending…' : 'Resend'}
                </Button>
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-end gap-2">
                {viewModel.pdfDownloadUrl ? (
                  <Button
                    variant="secondary"
                    size="sm"
                    iconLeft="download"
                    disabled={isDownloadingPdf}
                    onClick={() => {
                      void handleDownloadPdf();
                    }}
                  >
                    {isDownloadingPdf ? 'Downloading…' : 'Download PDF'}
                  </Button>
                ) : null}
                {isSubmitted ? (
                  <>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={markIncomplete.isPending}
                      onClick={() => {
                        void handleMarkIncomplete();
                      }}
                    >
                      {markIncomplete.isPending ? 'Working…' : 'Mark incomplete'}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      iconLeft="refresh"
                      onClick={() => {
                        setResendOpen(true);
                      }}
                    >
                      Resend
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      iconLeft="check"
                      disabled={isApproving}
                      onClick={() => {
                        void handleApprove();
                      }}
                    >
                      {isRenderingPdf ? 'Preparing PDF…' : approve.isPending ? 'Approving…' : 'Approve'}
                    </Button>
                  </>
                ) : null}
              </div>
            )}
          </div>
        ) : undefined
      }
    >
      {isLoading || isTokenLoading || resolvedMedia.isLoading ? (
        <div className="flex min-h-[320px] items-center justify-center">
          <ElvaPageLoader />
        </div>
      ) : isError || !viewModel ? (
        <div className="px-6 py-8 text-center text-[14px] text-[var(--ink-3)]">
          {error instanceof Error
            ? error.message
            : 'Could not load this form. It may not have been started yet.'}
        </div>
      ) : (
        <>
          <PatientFormView
            embedded
            formName={form.name}
          {...(viewModel.intro ? { intro: viewModel.intro } : {})}
          blocks={viewModel.body}
          values={values}
          readOnly
          onChange={() => {
            /* read-only review — staff never alter patient answers (W1) */
          }}
          onSubmit={() => {
            /* read-only review — no submit */
          }}
          {...(mediaContext ? { mediaContext } : {})}
          />
          {isRenderingPdf
            ? createPortal(
                <div aria-hidden style={PDF_HOST_STYLE}>
                  <div
                    style={PDF_SNAPSHOT_STYLE}
                    ref={(node) => {
                      if (node) pdfSnapshotResolver.current?.(node);
                    }}
                  >
                    <PatientFormView
                      formName={form.name}
                      {...(viewModel.intro ? { intro: viewModel.intro } : {})}
                      blocks={viewModel.body}
                      values={values}
                      readOnly
                      onChange={() => undefined}
                      onSubmit={() => undefined}
                      {...(mediaContext ? { mediaContext } : {})}
                    />
                  </div>
                </div>,
                document.body,
              )
            : null}
        </>
      )}
    </Modal>
  );
}
